'use client'
import type { FC } from 'react'
import { useCallback } from 'react'
import { GrandSection } from 'ui'
import type {
  CartItem,
  DeliveryDTO,
  ProductLightDTO,
  StorefrontWidget,
  WidgetBlog
} from 'ecosystem'
import type { OverridesContextType } from 'ui/lib/overrides'
import { OverridesContextProvider } from 'ui/lib/overrides'
import { WidgetBlogPost } from '../widgets'
import { FeaturedProducts, type IFeaturedProducts } from '../../products'

export type BlogDetailPageStdConfig = {
  widget: StorefrontWidget<WidgetBlog>
  relatedProducts: ProductLightDTO[]
  deliveryOptions: DeliveryDTO[] | null
  onFeaturedProductBuy: (item: CartItem) => void
  cartItems: CartItem[]
  onProductRequest: (item: ProductLightDTO) => void
  overrides?: OverridesContextType
  paths: {
    productsUrlPath: string
  }
} & Pick<IFeaturedProducts, 'customComponents'>

interface BlogDetailPageStdProps {
  config: BlogDetailPageStdConfig
}

const BlogDetailPageStd: FC<BlogDetailPageStdProps> = ({
  config: {
    overrides,
    widget,
    relatedProducts,
    onFeaturedProductBuy,
    cartItems,
    paths,
    ...restConfig
  }
}) => {
  const handleAdd = useCallback(
    (product: ProductLightDTO) => {
      const itemInCart = cartItems.find((item) => item.id === product.id)

      const qty = (itemInCart?.qty ?? 0) + 1

      onFeaturedProductBuy({
        id: product.id,
        type: 'SINGLE',
        qty,
        meta: {
          singleType: {
            product
          }
        }
      })
    },
    [cartItems, onFeaturedProductBuy]
  )

  return (
    <OverridesContextProvider overrides={{ ...overrides }}>
      <WidgetBlogPost widgetId={widget.widget.name} {...{ widget }} />
      {relatedProducts.length > 0 && (
        <GrandSection sectionId="related-products">
          <FeaturedProducts
            items={relatedProducts}
            onBuy={handleAdd}
            productsUrlPath={paths.productsUrlPath}
            sectionId="blog-products-related"
            title={overrides?.productsTitle || 'Related products'}
            {...restConfig}
          />
        </GrandSection>
      )}
    </OverridesContextProvider>
  )
}

export default BlogDetailPageStd
